/* eslint-disable react/require-default-props */
import React, {
 useContext, useEffect, useState, useMemo
} from 'react'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router'
import { useSelector } from 'react-redux'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { setPageAnalytics } from '@apps/utils/Analytics'
import { ANALYTICS_EVENT_TYPES, SIM_OPTIONS } from '@apps/utils/constants'
import { RadioCardGroup, Typography } from '@telus/koodo-ui-kit'
import useTagManagerInfo from '../../../_engine/hooks/useTagManagerInfo'

export const SIM_CARD_IDS = {
  ESIM: SIM_OPTIONS.E_SIM,
  HAS_PHYSICAL: SIM_OPTIONS.H_SIM,
  BUY_PHYSICAL: SIM_OPTIONS.P_SIM
}

const SimSelectionRadioCardGroup = ({
  heading,
  checkedId,
  onChange,
  items,
  testId = 'sim-selection-radio-cards',
  name = 'sim-selection'
}) => {
  const location = useLocation()
  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.CURRENT_REGION]: region
  } = useContext(ApplicationContext)

  const { simError } = useSelector((state) => state.simSelectionPage)
  const { currentPageModule } = useSelector(
    (state) => state.simSelectionPage.selectSimType || {}
  )

  const tagManagerInfo = useTagManagerInfo()
  const [pendingAnalytics, setPendingAnalytics] = useState(null)

  const resolvedHeading = typeof heading === 'string' ? heading : ''
  const resolvedName = name || 'sim-selection'
  const accessibilityLabel = resolvedHeading || resolvedName.replace(/[-_]+/g, ' ').trim() || 'sim selection'
  const resolvedItems = useMemo(() => {
    const sourceItems = Array.isArray(items) ? items : []

    return sourceItems.map((item) => ({
      ...item,
      id: item?.id || '',
      title: typeof item?.title === 'string' ? item.title : ''
    }))
  }, [items])

  useEffect(() => {
    if (pendingAnalytics && currentPageModule) {
      const analyticsInfo = {
        ...tagManagerInfo,
        click_text: pendingAnalytics.selectedOptionText,
        field_warning: simError || 'NA',
        journey_type: pendingAnalytics.journeyType || currentPageModule || 'NA'
      }

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(
        setPageAnalytics(
          location,
          'click',
          region,
          language,
          analyticsInfo,
          pendingAnalytics.selectedOptionText,
          ANALYTICS_EVENT_TYPES.CLICK
        )
      )

      setPendingAnalytics(null)
    }
  }, [pendingAnalytics, currentPageModule, simError, tagManagerInfo, location, region, language])

  const handleChange = (nextCheckedId) => {
    const selected = resolvedItems.find((item) => item.id === nextCheckedId)
    if (onChange) onChange(nextCheckedId)
    if (selected?.title) {
      setPendingAnalytics({
        selectedOptionText: selected.title,
        journeyType: selected.journeyType
      })
    }
  }

  const radioCardTokens = ({ checked }) => ({
    backgroundColor: checked ? '#000000' : '#ffffff',
    borderColor: checked ? '#000000' : '#c9c8c8',
    borderRadius: 4,
    borderWidth: 1,
    color: checked ? '#ffffff' : '#000000',
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 1.5,
    minWidth: 0,
    paddingBottom: 12,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 12,
    radioCheckedBackgroundColor: checked ? '#ffffff' : '#000000',
    radioCheckedSize: 16,
    radioInputBackgroundColor: checked ? '#000000' : '#ffffff',
    radioInputBorderColor: checked ? '#ffffff' : '#000000',
    radioInputBorderWidth: 2,
    radioInputSize: 24,
    radioOuterBorderColor: 'transparent',
    radioOuterBorderGap: 0,
    radioOuterBorderWidth: 0,
    radioSpace: 2
  })

  return (
    <div className="text-black" data-testid={testId}>
      {resolvedHeading && (
        <div className="mb-4">
          <Typography
            heading="h2"
            variant={{ size: 24, bold: true, color: '#000000' }}
            tokens={{
              color: '#000000',
              fontName: 'StagSans',
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: 0,
              lineHeight: '32px'
            }}
          >
            {resolvedHeading}
          </Typography>
        </div>
      )}
      <RadioCardGroup
        accessibilityLabel={accessibilityLabel}
        checkedId={checkedId || ''}
        items={resolvedItems}
        name={resolvedName}
        onChange={handleChange}
        radioCardTokens={radioCardTokens}
        tokens={{
          direction: 'column',
          fieldSpace: 0,
          space: 3
        }}
        variant={{ fullWidth: true }}
      />
    </div>
  )
}

SimSelectionRadioCardGroup.propTypes = {
  heading: PropTypes.string,
  checkedId: PropTypes.string,
  onChange: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      journeyType: PropTypes.string
    })
  ),
  testId: PropTypes.string,
  name: PropTypes.string
}

export default SimSelectionRadioCardGroup
